import { useCallback } from "react";
import { getJointMoveDisabledReason } from "../../../utils/jointTypeValidation";
import { prunePrimarySelectionIfStaleAfterSceneGraphMutation } from "../helpers";

export function useJointContextMenuActions({
  isPhysicsRunning,
  bodies,
  joints,
  contextMenuJointIds,
  sceneActions,
  clearBatchSelection,
  runGroupedBodyAction,
}) {
  const getJointParentBodyUuid = useCallback(
    (jointId) => {
      if (!jointId) return null;
      for (const body of Object.values(bodies || {})) {
        if (!body?.uuid || !Array.isArray(body.joints)) continue;
        if (
          body.joints.some((entry) =>
            typeof entry === "string" ? entry === jointId : entry?.uuid === jointId
          )
        ) {
          return body.uuid;
        }
      }
      return null;
    },
    [bodies]
  );

  const handleDeleteJoints = useCallback(
    (jointIds) => {
      if (isPhysicsRunning || !Array.isArray(jointIds) || jointIds.length === 0) return;
      runGroupedBodyAction(() => {
        jointIds.forEach((jointId) => {
          sceneActions.removeJoint(jointId);
        });
      });
      prunePrimarySelectionIfStaleAfterSceneGraphMutation();
      clearBatchSelection();
    },
    [isPhysicsRunning, runGroupedBodyAction, sceneActions, clearBatchSelection]
  );

  const getJointReparentTargetDisabledReason = useCallback(
    (targetBodyUuid, jointIds = contextMenuJointIds) => {
      if (!targetBodyUuid || !Array.isArray(jointIds)) return "Choose a target body.";
      const incomingKinds = [];
      for (const jointId of jointIds) {
        const currentParent = getJointParentBodyUuid(jointId);
        if (!currentParent || currentParent === targetBodyUuid) continue;
        const joint = joints?.[jointId];
        if (joint?.kind) incomingKinds.push(joint.kind);
        const reason = getJointMoveDisabledReason({
          joint,
          jointUuid: jointId,
          bodies,
          joints,
          sourceBodyUuid: currentParent,
          targetBodyUuid,
        });
        if (reason) {
          return jointIds.length > 1 ? `One selected joint cannot move here: ${reason}` : reason;
        }
      }
      if (incomingKinds.includes("free") && incomingKinds.length > 1) {
        return "Free joints must be the only joint on their body.";
      }
      const incomingRotationalCount = incomingKinds.filter(
        (kind) => kind === "ball" || kind === "hinge"
      ).length;
      if (incomingKinds.includes("ball") && incomingRotationalCount > 1) {
        return "Ball joints cannot move together with hinge or ball joints onto the same body.";
      }
      return "";
    },
    [bodies, contextMenuJointIds, getJointParentBodyUuid, joints]
  );

  const handleMoveJointsToBody = useCallback(
    (targetBodyUuid, jointIds = contextMenuJointIds) => {
      if (
        isPhysicsRunning ||
        !targetBodyUuid ||
        !Array.isArray(jointIds) ||
        jointIds.length === 0
      ) {
        return;
      }
      if (getJointReparentTargetDisabledReason(targetBodyUuid, jointIds)) return;

      const jointsToMove = jointIds.filter((jointId) => {
        const currentParent = getJointParentBodyUuid(jointId);
        return currentParent && currentParent !== targetBodyUuid;
      });
      if (jointsToMove.length === 0) {
        clearBatchSelection();
        return;
      }

      runGroupedBodyAction(() => {
        jointsToMove.forEach((jointId) => {
          sceneActions.moveJointToBody(jointId, targetBodyUuid);
        });
      });
      clearBatchSelection();
    },
    [
      isPhysicsRunning,
      contextMenuJointIds,
      getJointParentBodyUuid,
      getJointReparentTargetDisabledReason,
      runGroupedBodyAction,
      sceneActions,
      clearBatchSelection,
    ]
  );

  return {
    handleDeleteJoints,
    getJointReparentTargetDisabledReason,
    handleMoveJointsToBody,
  };
}
